// ═══════════════════════════════════════════════════════════════
// PA Grants — Project Worksheet Versions
// FEMA PA amendment tracking with cost line items
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Paper, Text, Group, Stack, Badge, Button, Table, Modal,
  Textarea, Select, TextInput,
} from '../../../components/gab/GabComponents';
import {
  IconPlus, IconCheck, IconX, IconEdit, IconSend, IconHistory,
  IconAlertCircle, IconTrash,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useAuthStore } from '../../../store/authStore';
import { useRoleStore } from '../../../store/roleStore';
import { notifications } from '../../../lib/notifications';
import { fmtDate } from '../../../lib/date-format';
import type { PAProject, PAProjectVersion, PAVersionLineItem, AmendmentType, VersionStatus } from '../../../types/pagrants';
import {
  AMENDMENT_TYPE_LABELS, VERSION_STATUS_LABELS, VERSION_STATUS_COLORS,
} from '../../../types/pagrants';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];
const CATEGORY_LABELS: Record<string, string> = {
  labor: 'Labor', equipment: 'Equipment', materials: 'Materials',
  contract: 'Contracts', other: 'Other',
};

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function deltaChip(delta: number) {
  if (delta === 0) return null;
  const color = delta > 0 ? '#16a34a' : '#D33423';
  const sign  = delta > 0 ? '+' : '';
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`,
      borderRadius: 4, padding: '1px 6px' }}>
      {sign}{fmt(delta)}
    </span>
  );
}

// ── Line item row editor ────────────────────────────────────────
interface LIRowProps {
  item: Partial<PAVersionLineItem>;
  onChange: (item: Partial<PAVersionLineItem>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

function LineItemRow({ item, onChange, onDelete, readOnly }: LIRowProps) {
  const total = (item.quantity || 0) * (item.unitCost || 0);
  return (
    <Table.Tr>
      <Table.Td style={{ minWidth: 110 }}>
        {readOnly ? (
          <Text size="xs">{CATEGORY_LABELS[item.category || 'other']}</Text>
        ) : (
          <select
            value={item.category || 'other'}
            onChange={e => onChange({ ...item, category: e.target.value as any })}
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, width: '100%' }}
          >
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        )}
      </Table.Td>
      <Table.Td style={{ minWidth: 200 }}>
        {readOnly ? <Text size="xs">{item.description}</Text> : (
          <input
            value={item.description || ''}
            onChange={e => onChange({ ...item, description: e.target.value })}
            placeholder="Description"
            style={{ fontSize: 12, padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: 4, width: '100%' }}
          />
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'right', minWidth: 70 }}>
        {readOnly ? <Text size="xs">{item.quantity}</Text> : (
          <input
            type="number" min={0} value={item.quantity ?? ''}
            onChange={e => onChange({ ...item, quantity: parseFloat(e.target.value) || 0 })}
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, width: 70, textAlign: 'right' }}
          />
        )}
      </Table.Td>
      <Table.Td style={{ minWidth: 80 }}>
        {readOnly ? <Text size="xs">{item.unit}</Text> : (
          <input
            value={item.unit || ''}
            onChange={e => onChange({ ...item, unit: e.target.value })}
            placeholder="hrs, ton…"
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, width: '100%' }}
          />
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'right', minWidth: 90 }}>
        {readOnly ? <Text size="xs">{fmt(item.unitCost || 0)}</Text> : (
          <input
            type="number" min={0} value={item.unitCost ?? ''}
            onChange={e => onChange({ ...item, unitCost: parseFloat(e.target.value) || 0 })}
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, width: 90, textAlign: 'right' }}
          />
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'right', minWidth: 90 }}>
        <Text size="xs" fw={600}>{fmt(total)}</Text>
      </Table.Td>
      {!readOnly && (
        <Table.Td>
          <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D33423', padding: 2 }}>
            <IconTrash size={14} />
          </button>
        </Table.Td>
      )}
    </Table.Tr>
  );
}

// ── Category subtotals ────────────────────────────────────────
function CategorySubtotals({ items }: { items: Partial<PAVersionLineItem>[] }) {
  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    items.forEach(li => {
      const cat = li.category || 'other';
      t[cat] = (t[cat] || 0) + (li.quantity || 0) * (li.unitCost || 0);
    });
    return t;
  }, [items]);

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {Object.entries(totals).map(([cat, total]) => (
        <div key={cat} style={{ fontSize: 12, color: '#555' }}>
          <span style={{ fontWeight: 600 }}>{CATEGORY_LABELS[cat] || cat}:</span> {fmt(total)}
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
interface Props {
  project: PAProject;
}

export default function ProjectVersions({ project }: Props) {
  const {
    versions, versionsLoading, lineItems,
    fetchVersions, fetchLineItems,
    createVersion, updateVersion, approveVersion, denyVersion, ensureVersionZero,
    createLineItem, updateLineItem, deleteLineItem,
  } = usePAGrantsStore();

  const { user } = useAuthStore();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicant = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editingItems, setEditingItems] = useState<Partial<PAVersionLineItem>[] | null>(null);
  const [savingItems, setSavingItems] = useState(false);
  const [showNewAmendment, setShowNewAmendment] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState<number | null>(null);
  const [denyNotes, setDenyNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewingId, setReviewingId] = useState<number | null>(null);

  const [newForm, setNewForm] = useState({
    amendmentType: 'cost_overrun' as AmendmentType,
    scopeNarrative: '',
    justification: '',
    totalCost: '',
    federalShare: '',
    applicantMatch: '',
  });

  useEffect(() => {
    fetchVersions(project.Rid);
  }, [project.Rid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-initialize V0
  useEffect(() => {
    if (!versionsLoading && versions.length === 0 && user?.email) {
      ensureVersionZero(project, user.email);
    }
  }, [versionsLoading, versions.length, project.Rid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select first version on load
  useEffect(() => {
    if (versions.length > 0 && selectedId === null) {
      setSelectedId(versions[versions.length - 1].id); // select latest by default
    }
  }, [versions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load line items when selection changes
  useEffect(() => {
    if (selectedId) {
      fetchLineItems(selectedId);
      setEditingItems(null);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedVersion = versions.find(v => v.id === selectedId) ?? null;
  const myLineItems = lineItems.filter(li => String(li.versionId) === String(selectedId));
  const lineTotal = useMemo(
    () => (editingItems ?? myLineItems).reduce((s, li) => s + (li.quantity || 0) * (li.unitCost || 0), 0),
    [editingItems, myLineItems],
  );

  const hasPendingDraft = versions.some(v => v.status === 'draft' || v.status === 'submitted' || v.status === 'under_review');
  const canEdit = selectedVersion?.status === 'draft';
  const canSubmit = selectedVersion?.status === 'draft';
  const canReview = (selectedVersion?.status === 'submitted' || selectedVersion?.status === 'under_review') && !isApplicant;

  async function handleSubmit() {
    if (!selectedVersion) return;
    setSubmitting(true);
    try {
      await updateVersion(selectedVersion.id, {
        status: 'submitted',
        submittedBy: user?.email ?? '',
        submittedDate: new Date().toISOString(),
      });
      notifications.success('Amendment submitted for review');
    } finally { setSubmitting(false); }
  }

  async function handleMarkUnderReview() {
    if (!selectedVersion) return;
    setReviewingId(selectedVersion.id);
    await updateVersion(selectedVersion.id, { status: 'under_review' });
    setReviewingId(null);
  }

  async function handleApprove() {
    if (!selectedVersion) return;
    setReviewingId(selectedVersion.id);
    try {
      await approveVersion(selectedVersion.id, project.Rid, user?.email ?? '', '');
      notifications.success('Version approved — project costs updated');
    } finally { setReviewingId(null); }
  }

  async function handleDeny() {
    if (!showDenyModal) return;
    setReviewingId(showDenyModal);
    await denyVersion(showDenyModal, user?.email ?? '', denyNotes);
    setShowDenyModal(null);
    setDenyNotes('');
    setReviewingId(null);
  }

  async function handleCreateAmendment() {
    const cost = newForm.totalCost ? parseFloat(newForm.totalCost) : null;
    const fedPct = parseFloat(project.federalSharePct || '75') || 75;
    const fedShare  = newForm.federalShare   ? parseFloat(newForm.federalShare)   : cost != null ? cost * (fedPct / 100) : 0;
    const appMatch  = newForm.applicantMatch ? parseFloat(newForm.applicantMatch) : cost != null ? cost * (1 - fedPct / 100) : 0;
    const prevApproved = [...versions].reverse().find(v => v.status === 'approved');
    const delta = cost != null ? cost - (prevApproved?.totalCost ?? 0) : 0;
    const nextNum = Math.max(...versions.map(v => v.versionNumber), -1) + 1;
    setSubmitting(true);
    try {
      const created = await createVersion({
        projectId:      String(project.Rid),
        versionNumber:  nextNum,
        amendmentType:  newForm.amendmentType,
        status:         'draft',
        scopeNarrative: newForm.scopeNarrative,
        justification:  newForm.justification,
        totalCost:      cost ?? 0,
        federalShare:   fedShare,
        applicantMatch: appMatch,
        costDelta:      delta,
        submittedBy:    user?.email ?? '',
      });
      if (created) {
        // Copy line items from the previous approved version
        if (prevApproved) {
          await fetchLineItems(prevApproved.id);
          const prevItems = usePAGrantsStore.getState().lineItems.filter(
            li => String(li.versionId) === String(prevApproved.id),
          );
          for (let i = 0; i < prevItems.length; i++) {
            const li = prevItems[i];
            await createLineItem({
              versionId:   String(created.id),
              category:    li.category,
              description: li.description,
              quantity:    li.quantity,
              unit:        li.unit,
              unitCost:    li.unitCost,
              notes:       li.notes,
              sortOrder:   i,
            });
          }
          // If no explicit cost was given, compute it from the copied line items
          if (cost == null && prevItems.length > 0) {
            const copiedTotal = prevItems.reduce((s, li) => s + li.quantity * li.unitCost, 0);
            await updateVersion(created.id, {
              totalCost:      copiedTotal,
              federalShare:   copiedTotal * (fedPct / 100),
              applicantMatch: copiedTotal * (1 - fedPct / 100),
              costDelta:      copiedTotal - (prevApproved.totalCost ?? 0),
            });
          }
          await fetchLineItems(created.id);
        }
        setSelectedId(created.id);
      }
      setShowNewAmendment(false);
      setNewForm({ amendmentType: 'cost_overrun', scopeNarrative: '', justification: '', totalCost: '', federalShare: '', applicantMatch: '' });
    } finally { setSubmitting(false); }
  }

  async function handleSaveLineItems() {
    if (!editingItems || !selectedId) return;
    setSavingItems(true);
    try {
      // Delete all existing, create new
      await Promise.all(myLineItems.map(li => deleteLineItem(li.id)));
      for (let i = 0; i < editingItems.length; i++) {
        await createLineItem({ ...editingItems[i], versionId: String(selectedId), sortOrder: i });
      }
      // Refresh
      await fetchLineItems(selectedId);
      // Update version total
      const total = editingItems.reduce((s, li) => s + (li.quantity || 0) * (li.unitCost || 0), 0);
      const fedPct = parseFloat(project.federalSharePct || '75') || 75;
      await updateVersion(selectedId, {
        totalCost:      total,
        federalShare:   total * (fedPct / 100),
        applicantMatch: total * (1 - fedPct / 100),
      });
      setEditingItems(null);
      notifications.success('Line items saved');
    } finally { setSavingItems(false); }
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={600}>Project Worksheet Versions</Text>
          <Text size="xs" c="dimmed">
            FEMA PA version history — each approved version represents an obligated cost snapshot
          </Text>
        </div>
        {!hasPendingDraft && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setShowNewAmendment(true)}>
            Request Amendment
          </Button>
        )}
        {hasPendingDraft && (
          <Badge size="sm" variant="light" style={{ background: '#fff7ed', color: '#C68700', border: '1px solid #fed7aa' }}>
            Amendment Pending
          </Badge>
        )}
      </Group>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Version timeline sidebar */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <Stack gap={4}>
            {versions.map((v, i) => {
              const isActive = v.id === selectedId;
              const statusColor = VERSION_STATUS_COLORS[v.status] || '#939598';
              return (
                <button
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px',
                    border: `1px solid ${isActive ? '#4B3FFF' : '#e2e8f0'}`,
                    borderRadius: 6, background: isActive ? '#f0eeff' : '#fff',
                    cursor: 'pointer', position: 'relative',
                  }}
                >
                  {/* Timeline line */}
                  {i < versions.length - 1 && (
                    <div style={{
                      position: 'absolute', left: 19, bottom: -5, width: 2, height: 5,
                      background: '#e2e8f0', zIndex: 0,
                    }} />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: v.status === 'approved' ? '#16a34a' : statusColor,
                      flexShrink: 0,
                    }} />
                    <Text size="xs" fw={700} style={{ color: isActive ? '#4B3FFF' : '#1e293b' }}>
                      V{v.versionNumber}
                    </Text>
                  </div>
                  <Text size="xs" c="dimmed" style={{ fontSize: 10, lineHeight: 1.3 }}>
                    {AMENDMENT_TYPE_LABELS[v.amendmentType]?.replace(' (V0)', '')}
                  </Text>
                  <Text size="xs" fw={600} style={{ marginTop: 2 }}>{fmt(v.totalCost)}</Text>
                  {v.status !== 'approved' && (
                    <div style={{ marginTop: 4 }}>
                      <Badge size="xs" variant="light" style={{ background: `${statusColor}18`, color: statusColor }}>
                        {VERSION_STATUS_LABELS[v.status]}
                      </Badge>
                    </div>
                  )}
                </button>
              );
            })}
          </Stack>
        </div>

        {/* Version detail */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selectedVersion ? (
            <Paper withBorder p="xl" radius="md" style={{ textAlign: 'center' }}>
              <IconHistory size={32} color="#cbd5e1" style={{ marginBottom: 8 }} />
              <Text size="sm" c="dimmed">Select a version to view details</Text>
            </Paper>
          ) : (
            <Stack gap="md">
              {/* Version header */}
              <Paper withBorder p="md" radius="md">
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <Text fw={700} size="sm">Version {selectedVersion.versionNumber}</Text>
                    <Badge size="sm" variant="light"
                      style={{ background: `${VERSION_STATUS_COLORS[selectedVersion.status]}18`, color: VERSION_STATUS_COLORS[selectedVersion.status] }}>
                      {VERSION_STATUS_LABELS[selectedVersion.status]}
                    </Badge>
                    <Badge size="sm" variant="outline" color="gray">
                      {AMENDMENT_TYPE_LABELS[selectedVersion.amendmentType]}
                    </Badge>
                  </Group>
                  {selectedVersion.costDelta !== 0 && deltaChip(selectedVersion.costDelta)}
                </Group>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[
                    { label: 'Total Cost',      value: fmt(selectedVersion.totalCost) },
                    { label: 'Federal Share',   value: fmt(selectedVersion.federalShare) },
                    { label: 'Applicant Match', value: fmt(selectedVersion.applicantMatch) },
                  ].map(c => (
                    <div key={c.label} style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px' }}>
                      <Text size="xs" c="dimmed" tt="uppercase" style={{ fontSize: 10 }}>{c.label}</Text>
                      <Text size="sm" fw={700}>{c.value}</Text>
                    </div>
                  ))}
                </div>

                {selectedVersion.submittedDate && (
                  <Text size="xs" c="dimmed" mt="xs">
                    Submitted {fmtDate(selectedVersion.submittedDate)}
                    {selectedVersion.submittedBy && ` by ${selectedVersion.submittedBy}`}
                    {selectedVersion.approvedDate && ` · Approved ${fmtDate(selectedVersion.approvedDate)}`}
                  </Text>
                )}
              </Paper>

              {/* Scope & Justification */}
              {(selectedVersion.scopeNarrative || selectedVersion.justification) && (
                <Paper withBorder p="md" radius="md">
                  {selectedVersion.scopeNarrative && (
                    <>
                      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>Scope of Work</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedVersion.scopeNarrative}</Text>
                    </>
                  )}
                  {selectedVersion.justification && selectedVersion.versionNumber > 0 && (
                    <>
                      <Text size="xs" fw={600} tt="uppercase" c="dimmed" mt="sm" mb={4}>Justification</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedVersion.justification}</Text>
                    </>
                  )}
                </Paper>
              )}

              {/* Review notes (if denied or reviewed) */}
              {selectedVersion.reviewNotes && (
                <Paper withBorder p="sm" radius="md"
                  style={{ borderLeft: `3px solid ${selectedVersion.status === 'denied' ? '#D33423' : '#4B3FFF'}` }}>
                  <Group gap="xs" mb={4}>
                    <IconAlertCircle size={14} color={selectedVersion.status === 'denied' ? '#D33423' : '#4B3FFF'} />
                    <Text size="xs" fw={600}>
                      {selectedVersion.status === 'denied' ? 'Denial Reason' : 'Review Notes'}
                    </Text>
                  </Group>
                  <Text size="sm">{selectedVersion.reviewNotes}</Text>
                  {selectedVersion.reviewedBy && (
                    <Text size="xs" c="dimmed" mt={4}>— {selectedVersion.reviewedBy}</Text>
                  )}
                </Paper>
              )}

              {/* Line items */}
              <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text size="xs" fw={600} tt="uppercase" c="dimmed">Cost Line Items</Text>
                  {canEdit && editingItems === null && (
                    <Button size="xs" variant="subtle" leftSection={<IconEdit size={12} />}
                      onClick={() => setEditingItems([...myLineItems])}>
                      Edit Line Items
                    </Button>
                  )}
                  {canEdit && editingItems !== null && (
                    <Group gap="xs">
                      <Button size="xs" variant="subtle" onClick={() => setEditingItems(null)}>Cancel</Button>
                      <Button size="xs" loading={savingItems} onClick={handleSaveLineItems}>Save</Button>
                    </Group>
                  )}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <Table striped>
                    <Table.Thead style={{ background: '#f8fafc' }}>
                      <Table.Tr>
                        <Table.Th>Category</Table.Th>
                        <Table.Th>Description</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
                        <Table.Th>Unit</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Unit Cost</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                        {editingItems !== null && <Table.Th />}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(editingItems ?? myLineItems).length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: 13 }}>
                            {editingItems !== null ? 'Add line items below' : 'No line items recorded'}
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        (editingItems ?? myLineItems).map((li, i) => (
                          <LineItemRow
                            key={i}
                            item={li}
                            readOnly={editingItems === null}
                            onChange={updated => setEditingItems(prev => prev!.map((x, j) => j === i ? updated : x))}
                            onDelete={() => setEditingItems(prev => prev!.filter((_, j) => j !== i))}
                          />
                        ))
                      )}
                    </Table.Tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={editingItems !== null ? 7 : 6} style={{ padding: '10px 14px' }}>
                          <Group justify="space-between" align="center">
                            <CategorySubtotals items={editingItems ?? myLineItems} />
                            <Text fw={700} size="sm">Total: {fmt(lineTotal)}</Text>
                          </Group>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
                {editingItems !== null && (
                  <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9' }}>
                    <Button size="xs" variant="subtle" leftSection={<IconPlus size={12} />}
                      onClick={() => setEditingItems(prev => [...(prev ?? []), { category: 'labor', quantity: 1, unitCost: 0 }])}>
                      Add Line
                    </Button>
                  </div>
                )}
              </Paper>

              {/* Action buttons */}
              <Group justify="flex-end" gap="sm">
                {canSubmit && (
                  <Button
                    size="xs"
                    leftSection={<IconSend size={14} />}
                    loading={submitting}
                    onClick={handleSubmit}
                  >
                    Submit for Review
                  </Button>
                )}
                {canReview && (
                  <>
                    {selectedVersion.status === 'submitted' && (
                      <Button size="xs" variant="subtle"
                        loading={reviewingId === selectedVersion.id}
                        onClick={handleMarkUnderReview}>
                        Mark Under Review
                      </Button>
                    )}
                    <Button size="xs" variant="outline" color="red"
                      leftSection={<IconX size={14} />}
                      onClick={() => setShowDenyModal(selectedVersion.id)}>
                      Deny
                    </Button>
                    <Button size="xs" color="green"
                      leftSection={<IconCheck size={14} />}
                      loading={reviewingId === selectedVersion.id}
                      onClick={handleApprove}>
                      Approve
                    </Button>
                  </>
                )}
              </Group>
            </Stack>
          )}
        </div>
      </div>

      {/* New Amendment Modal */}
      <Modal
        opened={showNewAmendment}
        onClose={() => setShowNewAmendment(false)}
        title="Request Amendment"
        size="lg"
      >
        <Stack gap="sm">
          <Select
            label="Amendment Type"
            required
            data={
              (Object.entries(AMENDMENT_TYPE_LABELS) as [AmendmentType, string][])
                .filter(([k]) => k !== 'initial')
                .map(([value, label]) => ({ value, label }))
            }
            value={newForm.amendmentType}
            onChange={v => setNewForm(f => ({ ...f, amendmentType: (v as AmendmentType) || 'cost_overrun' }))}
          />
          <Textarea
            label="Scope of Work (updated)"
            placeholder="Describe any changes to the scope of work…"
            rows={3}
            value={newForm.scopeNarrative}
            onChange={e => setNewForm(f => ({ ...f, scopeNarrative: e.target.value }))}
          />
          <Textarea
            label="Justification"
            placeholder="Explain why this amendment is needed…"
            rows={3}
            required
            value={newForm.justification}
            onChange={e => setNewForm(f => ({ ...f, justification: e.target.value }))}
          />
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Revised Total Cost ($)
            </label>
            <input
              type="number" min={0} step={0.01} placeholder="Leave blank to fill in later"
              value={newForm.totalCost}
              onChange={e => setNewForm(f => ({ ...f, totalCost: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14 }}
            />
          </div>
          <Group grow>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Federal Share ($)</label>
              <input
                type="number" min={0} step={0.01} placeholder={`Auto (${project.federalSharePct || 75}%)`}
                value={newForm.federalShare}
                onChange={e => setNewForm(f => ({ ...f, federalShare: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Applicant Match ($)</label>
              <input
                type="number" min={0} step={0.01} placeholder="Auto"
                value={newForm.applicantMatch}
                onChange={e => setNewForm(f => ({ ...f, applicantMatch: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14 }}
              />
            </div>
          </Group>
          <Text size="xs" c="dimmed">
            A draft version will be created. You can add line items before submitting for review.
          </Text>
          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="subtle" onClick={() => setShowNewAmendment(false)}>Cancel</Button>
            <Button
              loading={submitting}
              disabled={!newForm.justification}
              onClick={handleCreateAmendment}
            >
              Create Draft
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Deny Modal */}
      <Modal
        opened={showDenyModal !== null}
        onClose={() => setShowDenyModal(null)}
        title="Deny Amendment"
        size="sm"
      >
        <Stack gap="sm">
          <Textarea
            label="Reason for Denial"
            placeholder="Explain why this amendment is being denied…"
            rows={4}
            required
            value={denyNotes}
            onChange={e => setDenyNotes(e.target.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setShowDenyModal(null)}>Cancel</Button>
            <Button color="red" disabled={!denyNotes} loading={reviewingId !== null} onClick={handleDeny}>
              Deny Amendment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
